import { Color, Spacing } from '@persona-id/design-tokens';
import { MonitorIcon, TransferOutIcon } from '@persona-id/design-tokens/icons';
import { saveAs } from 'file-saver';
import { noop } from 'lodash/fp';
import { ReactNode, RefObject, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom-v5-compat';
import { isPristine } from 'redux-form';
import styled from 'styled-components';

import useEditorSubPath from 'dashboard/apps/InquiryTemplateEditor/hooks/useEditorSubPath';
import {
  FieldReferenceConfig,
  FieldReferenceMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateConfigEditor/utils';
import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import {
  FieldSchemaEditorTable,
  RelationSchemaType,
} from 'dashboard/components-lib/FieldSchemaEditor';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { paragraphMixins } from 'design-system/mixins/text';
import { FieldSchema } from 'lib/fields';
import { ApiEndpoint } from 'lib/interfaces/api';

export const emptyTableCellLabel = '--';

const ReferencesColumn = styled.div`
  ${paragraphMixins.sm};
  display: -webkit-box;
  overflow: hidden;
  white-space: normal;
  text-overflow: clip;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 20px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: auto;
  width: 100%;
  padding: 0 ${Spacing[5]};
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.secondary};
`;

const ReferenceCount = styled.span`
  color: ${({ theme }) => theme.color.text.secondary};
  font-size: 14px;
`;

const AdditionalReferencesLabel = styled(Paragraph).attrs(() => ({ size: 'sm' }))`
  white-space: pre-wrap;
  line-height: ${Spacing[5]};
`;

const DISPLAYED_COUNT = 1;
const generateAdditionalReferencesLabels = (
  references: FieldReferenceConfig[],
  editorBasePath: string,
): ReactNode => {
  const displayedReferences = references.length > 0 ? references.slice(0, DISPLAYED_COUNT) : [];
  const additionalReferences = references.slice(displayedReferences.length);

  const additionalDisplay =
    additionalReferences.length > 0 ? (
      <Tooltip
        side='top'
        trigger={
          <span>
            {`, `}
            <ReferenceCount>{`...${additionalReferences.length} more`}</ReferenceCount>
          </span>
        }
      >
        <AdditionalReferencesLabel size='sm'>
          {additionalReferences.map((reference, index) => (
            <>
              <Link key={index} to={`${editorBasePath}${reference.referenceSubPath}`}>
                {reference.referenceLabel}
              </Link>
              {'\n'}
            </>
          ))}
        </AdditionalReferencesLabel>
      </Tooltip>
    ) : null;

  return references.length > 0 ? (
    <>
      {displayedReferences.map((reference, index) => (
        <>
          <Link key={index} to={`${editorBasePath}${reference.referenceSubPath}`}>
            {reference.referenceLabel}
          </Link>
          {index !== displayedReferences.length - 1 && ', '}
        </>
      ))}
      {additionalDisplay}
    </>
  ) : (
    <>{emptyTableCellLabel}</>
  );
};

export const getDefaultValueDisplay = (defaultValue: string | boolean | undefined): string => {
  if (defaultValue == null) {
    return emptyTableCellLabel;
  } else if (typeof defaultValue === 'boolean') {
    return defaultValue ? 'True' : 'False';
  } else {
    return defaultValue;
  }
};

interface FieldSchemaEditorCardProps {
  fieldSchema: FieldSchema[];
  fieldReferenceMap: FieldReferenceMap;
  currentFieldKey: string | undefined;
  versionId: string;
  editable: boolean;
  openAddFieldPanel: () => void;
  selectFieldKeyToEdit: (fieldKey: string | undefined) => void;
  title: string;
  rightColumnRef: RefObject<HTMLDivElement>;
}

const FieldSchemaEditorCard = ({
  fieldSchema,
  fieldReferenceMap,
  currentFieldKey,
  versionId,
  editable,
  openAddFieldPanel,
  selectFieldKeyToEdit,
  title,
  rightColumnRef,
}: FieldSchemaEditorCardProps) => {
  const { fieldKey } = useParams<{
    fieldKey?: string;
  }>();

  useEffect(() => {
    if (fieldKey) {
      selectFieldKeyToEdit(fieldKey);
    }
  }, []);

  const sortedFields = useMemo(
    () =>
      fieldSchema
        .filter((field) => field?.label != null && field?.key != null)
        .sort(({ label: a }, { label: b }) => a.localeCompare(b)),
    [fieldSchema],
  );

  const editorBasePath = useEditorSubPath({ editorSubPath: 'config', onlyBasePath: true });

  const dispatch = useDispatch();
  const isFormPristine = useSelector<RootState, boolean>((state) =>
    isPristine(INQUIRY_TEMPLATE_EDITOR_FORM_NAME)(state),
  );
  const handleExportFieldsCsv = useCallback(async () => {
    try {
      const blob = await SessionClientDashboard.getBlob(
        ApiEndpoint.InquiryTemplateVersion,
        versionId,
        'export-csv',
      );
      saveAs(blob, `${versionId}_fields.csv`);
    } catch (e) {
      dispatch(showMessage('Failed to export fields. Please try again.', MessageType.Failure));
    }
  }, [versionId, dispatch]);

  return (
    <RightColumn ref={rightColumnRef}>
      <Spacer height={3} />
      <Heading size='sm'>{title}</Heading>
      <Spacer height={3} />
      <Card>
        <Card.Body>
          <Card.Title
            actions={
              <HStack equal>
                {editable && (
                  <Button
                    width='auto'
                    variant='basic'
                    id='fields-table__add-field'
                    aria-label='Add field'
                    data-cy='fields-table__add-button'
                    onClick={openAddFieldPanel}
                  >
                    Add field
                  </Button>
                )}
                <Tooltip
                  side='top'
                  trigger={
                    <Button
                      width='auto'
                      variant='basic'
                      id='fields-table__export-fields'
                      aria-label='Export'
                      prefixIcon={<TransferOutIcon />}
                      disabled={fieldSchema.length === 0 || !isFormPristine}
                      onClick={handleExportFieldsCsv}
                    >
                      Export
                    </Button>
                  }
                  disabled={isFormPristine}
                >
                  Save your changes to export fields data.
                </Tooltip>
              </HStack>
            }
          >
            Manage fields
          </Card.Title>
          <FieldSchemaEditorTable
            inputSchema={sortedFields}
            productIcon={<MonitorIcon size={36} color={Color.BorderIconSecondary} />}
            customColumns={[
              {
                header: 'Referenced by',
                generateContent: ({ field }) => (
                  <ReferencesColumn>
                    {generateAdditionalReferencesLabels(
                      Array.from(fieldReferenceMap[field.key] ?? []),
                      editorBasePath,
                    )}
                  </ReferencesColumn>
                ),
              },
              {
                header: 'Default value',
                width: '120px',
                generateContent: ({ field }) =>
                  field.type !== RelationSchemaType.RelationSchema
                    ? getDefaultValueDisplay(field['default-value'])
                    : emptyTableCellLabel,
              },
            ]}
            nullState={<span>This inquiry template currently has no fields configured.</span>}
            focusedKey={currentFieldKey}
            // not actually uneditable, but the component currently displays an 'unsaved changes' badge
            // due to differences in how the parent is expected to manage form data
            uneditableKeys={fieldSchema.map(({ key }) => key)}
            editable={editable}
            onUpdateSchema={noop}
            onFocusSchema={selectFieldKeyToEdit}
          />
        </Card.Body>
      </Card>
    </RightColumn>
  );
};

export default FieldSchemaEditorCard;
